import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { DropZone } from "@/components/DropZone";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useUnsavedGuard } from "@/context/UnsavedChangesGuard";
import { api, euro, fmtDate, formatApiErrorDetail, openAuthPdf, downloadAuthPdf } from "@/lib/api";
import { InternalLayout } from "@/components/InternalLayout";
import { useSuspendedClientGuard } from "@/context/SuspendedClientAlert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MultiSelect } from "@/components/MultiSelect";
import { RichEditor } from "@/components/RichEditor";

const RUOLO_OPTIONS = ["Junior", "Medium", "Senior"];
const toArr = (v) => Array.isArray(v) ? v : (v ? [v] : []);
import { toast } from "sonner";
import { ArrowLeftIcon as ArrowLeft, PlusIcon as Plus, TrashIcon as Trash, PaperclipIcon as Paperclip, DownloadSimpleIcon as DownloadSimple, MoonIcon as Moon, UsersThreeIcon as UsersThree, PackageIcon as Package, ClockCounterClockwiseIcon as ClockCounterClockwise, FloppyDiskIcon as FloppyDisk, ArrowSquareOutIcon as ArrowSquareOut, WrenchIcon as Wrench, LinkBreakIcon as LinkBreak, LinkSimpleIcon as LinkSimple, MagnifyingGlassIcon as MagnifyingGlass, ReceiptIcon as Receipt, WarningIcon as Warning, HeadsetIcon as Headset, CheckSquareIcon as CheckSquare, SquareIcon as Square } from "@phosphor-icons/react";

const downloadAllegato = async (lavId, meta) => {
  try {
    const res = await api.get(`/lavorazioni/${lavId}/allegati/${meta.id}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: meta.content_type || "application/octet-stream" }));
    const a = document.createElement("a"); a.href = url; a.download = meta.nome_originale || "allegato";
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  } catch { toast.error("Errore download allegato"); }
};

const ACTION_LABEL = {
  creazione: "Creazione", cambio_fase: "Cambio fase", cambio_stato_merce: "Cambio stato merce",
  modifica_ore: "Modifica ore previste", modifica_skill: "Modifica skill", allegato_aggiunto: "Allegato aggiunto",
  allegato_rimosso: "Allegato rimosso", visibilita: "Visibilità area cliente",
  creazione_da_preventivo: "Creata da preventivo", crea_intervento: "Intervento creato", scollega_intervento: "Intervento scollegato",
  collega_lavorazione: "Lavorazione collegata", scollega_lavorazione: "Lavorazione scollegata",
};
const FASE_INTERVENTO_TRIGGER = "da_pianificare";
const INT_STATO_LABEL = {
  da_pianificare: "Da pianificare", in_attesa_conferma: "In attesa di conferma",
  pianificata: "Fissato appuntamento", in_corso: "In lavorazione",
  completata: "Completato", sospesa: "Sospeso", annullata: "Annullato",
};
const intStatoLabel = (k) => INT_STATO_LABEL[k] || (k || "").replace(/_/g, " ");

export default function LavorazioneDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lav, setLav] = useState(null);
  useSuspendedClientGuard(lav?.cliente_id);
  const [cfg, setCfg] = useState({ skill: [], fasi: [], stati_merce: [] });
  const [tecnici, setTecnici] = useState([]);
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState(null);
  const [orig, setOrig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [intConfirmOpen, setIntConfirmOpen] = useState(false);
  const [intLinkDlg, setIntLinkDlg] = useState(false);
  const [intLinkQ, setIntLinkQ] = useState("");
  const [intLinkResults, setIntLinkResults] = useState([]);
  const [intLinkSel, setIntLinkSel] = useState([]);
  const [intLinkConfirm, setIntLinkConfirm] = useState(false);
  const [lavLinkDlg, setLavLinkDlg] = useState(false);
  const [lavLinkQ, setLavLinkQ] = useState("");
  const [lavLinkResults, setLavLinkResults] = useState([]);
  const [prevBox, setPrevBox] = useState(null);
  const fileRef = useRef(null);

  const faseMap = useMemo(() => Object.fromEntries((cfg.fasi || []).map((f) => [f.key, f])), [cfg]);

  const load = useCallback(() => {
    api.get(`/lavorazioni/${id}`).then(({ data }) => {
      setLav(data);
      const f0 = {
        cliente_id: data.cliente_id, titolo_lavorazione: data.titolo_lavorazione || "", data_conferma_ordine: (data.data_conferma_ordine || "").slice(0, 10),
        commerciale_id: data.commerciale_id || "", descrizione_lavorazione: data.descrizione_lavorazione || "",
        importo_vendita: data.importo_vendita ?? 0, merce_necessaria: data.merce_necessaria || "", stato_merce: data.stato_merce || "Non verificata",
        skill_richieste: data.skill_richieste || [], righe_tecnici_previsti: (data.righe_tecnici_previsti || []).map((r) => ({ ...r, descrizione: toArr(r.descrizione), skill: toArr(r.skill) })),
        data_prevista: (data.data_prevista || "").slice(0, 10), fase_lavorazione: data.fase_lavorazione || "in_attesa",
        pernotto_esterno: !!data.pernotto_esterno, manovalanza_esterna: !!data.manovalanza_esterna, remoto: !!data.remoto,
        visibile_area_cliente: data.visibile_area_cliente !== false, note_interne: data.note_interne || "",
        solo_fornitura: !!data.solo_fornitura,
      };
      setForm(f0); setOrig(JSON.stringify(f0));
    }).catch(() => toast.error("Lavorazione non trovata"));
    api.get(`/lavorazioni/${id}/logs`).then(({ data }) => setLogs(data)).catch(() => {});
    api.get(`/lavorazioni/${id}/preventivo`).then(({ data }) => setPrevBox(data)).catch(() => setPrevBox(null));
  }, [id]);

  useEffect(() => {
    api.get("/lavorazioni/settings").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/tecnici").then(({ data }) => setTecnici([...data].sort((a, b) => (a.name || "").localeCompare(b.name || "")))).catch(() => {});
    load();
  }, [load]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updRiga = (i, k, v) => setForm((f) => { const r = [...f.righe_tecnici_previsti]; r[i] = { ...r[i], [k]: v }; return { ...f, righe_tecnici_previsti: r }; });
  const addRiga = () => setForm((f) => ({ ...f, righe_tecnici_previsti: [...f.righe_tecnici_previsti, { descrizione: [], skill: [], numero_tecnici: 1, ore_per_tecnico: 0 }] }));
  const removeRiga = (i) => setForm((f) => ({ ...f, righe_tecnici_previsti: f.righe_tecnici_previsti.filter((_, j) => j !== i) }));

  const rigaOre = (r) => (parseInt(r.numero_tecnici) || 0) * (parseFloat(r.ore_per_tecnico) || 0);
  const oreProprie = useMemo(() => form ? form.righe_tecnici_previsti.reduce((s, r) => s + rigaOre(r), 0) : 0, [form]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form, importo_vendita: parseFloat(form.importo_vendita) || 0,
        righe_tecnici_previsti: form.righe_tecnici_previsti.map((r) => ({
          descrizione: toArr(r.descrizione), skill: toArr(r.skill), numero_tecnici: parseInt(r.numero_tecnici) || 1, ore_per_tecnico: parseFloat(r.ore_per_tecnico) || 0,
        })),
      };
      await api.put(`/lavorazioni/${id}`, payload);
      toast.success("Lavorazione salvata");
      const shouldOfferIntervento = form.fase_lavorazione === FASE_INTERVENTO_TRIGGER && !(lav.interventi_count > 0);
      load();
      if (shouldOfferIntervento) setIntConfirmOpen(true);
      return true;
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore salvataggio"); return false; }
    finally { setSaving(false); }
  };

  const dirty = useMemo(() => (form && orig ? JSON.stringify(form) !== orig : false), [form, orig]);
  const guardedNavigate = useUnsavedGuard(dirty, () => save());

  const openPrevPdf = async () => {
    if (!prevBox?.pdf_presente) { toast.error("PDF preventivo non disponibile."); return; }
    try { await openAuthPdf(`/crm/preventivi/${prevBox.preventivo_id}/pdf`); }
    catch { toast.error("PDF preventivo non disponibile."); }
  };
  const downloadPrevPdf = async () => {
    if (!prevBox?.pdf_presente) { toast.error("PDF preventivo non disponibile."); return; }
    try { await downloadAuthPdf(`/crm/preventivi/${prevBox.preventivo_id}/pdf`, `Preventivo ${prevBox.numero_preventivo}.pdf`); }
    catch { toast.error("PDF preventivo non disponibile."); }
  };

  const createIntervento = () => {
    setIntConfirmOpen(false);
    nav(`/interventi/nuovo?from_lavorazione=${id}`);
  };

  const scollegaIntervento = async (attivitaId) => {
    try { await api.post(`/lavorazioni/${id}/scollega-intervento/${attivitaId}`); toast.success("Intervento scollegato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore scollegamento"); }
  };

  const searchLinkInt = async (val) => {
    setIntLinkQ(val);
    try { const { data } = await api.get("/attivita/search", { params: { q: val } }); setIntLinkResults((data || []).slice(0, 30)); }
    catch { setIntLinkResults([]); }
  };
  const toggleIntLinkSel = (attivitaId) => {
    setIntLinkSel((prev) => prev.includes(attivitaId) ? prev.filter((x) => x !== attivitaId) : [...prev, attivitaId]);
  };
  const doLinkSelected = async () => {
    const ids = [...intLinkSel];
    if (ids.length === 0) return;
    try {
      const results = await Promise.allSettled(ids.map((aid) => api.post(`/lavorazioni/${id}/attivita/link`, { attivita_id: aid })));
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const ko = results.length - ok;
      if (ok > 0) toast.success(`${ok} intervent${ok === 1 ? "o" : "i"} collegat${ok === 1 ? "o" : "i"}`);
      if (ko > 0) toast.error(`${ko} collegament${ko === 1 ? "o" : "i"} non riuscit${ko === 1 ? "o" : "i"}`);
      setIntLinkConfirm(false); setIntLinkDlg(false); setIntLinkQ(""); setIntLinkResults([]); setIntLinkSel([]); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore collegamento"); }
  };
  const confirmLinkSelection = () => {
    if (intLinkSel.length === 0) return;
    if (intLinkSel.length > 1) { setIntLinkConfirm(true); return; }
    doLinkSelected();
  };

  const searchLinkLav = async (val) => {
    setLavLinkQ(val);
    try { const { data } = await api.get("/lavorazioni/search", { params: { q: val, exclude: id } }); setLavLinkResults((data || []).slice(0, 30)); }
    catch { setLavLinkResults([]); }
  };
  const linkLavorazione = async (lavId) => {
    const already = (lav?.lavorazioni_collegate || []).some((x) => x.id === lavId);
    if (already || lavId === id) { toast.info("Lavorazione già collegata"); return; }
    try { await api.post(`/lavorazioni/${id}/lavorazioni/link`, { lavorazione_id: lavId }); toast.success("Lavorazione collegata"); setLavLinkDlg(false); setLavLinkQ(""); setLavLinkResults([]); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore collegamento"); }
  };
  const scollegaLavorazione = async (lavId) => {
    try { await api.post(`/lavorazioni/${id}/lavorazioni/scollega/${lavId}`); toast.success("Lavorazione scollegata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore scollegamento"); }
  };

  const del = async () => {
    try { await api.delete(`/lavorazioni/${id}`); toast.success("Lavorazione eliminata"); nav("/lavorazioni"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try { await api.post(`/lavorazioni/${id}/allegati`, fd); toast.success("Allegato caricato"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore upload"); }
    if (fileRef.current) fileRef.current.value = "";
  };
  const delAllegato = async (aid) => {
    try { await api.delete(`/lavorazioni/${id}/allegati/${aid}`); toast.success("Allegato rimosso"); load(); }
    catch (e) { toast.error("Errore"); }
  };

  if (!lav || !form) return <InternalLayout><p className="text-sm text-muted-foreground">Caricamento…</p></InternalLayout>;

  const faseBadge = (key) => { const f = faseMap[key]; return <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-xs font-medium border" style={{ backgroundColor: (f?.color || "#94a3b8") + "20", color: f?.color || "#64748b", borderColor: (f?.color || "#94a3b8") + "55" }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: f?.color || "#94a3b8" }} />{f?.label || key}</span>; };

  return (
    <InternalLayout>
      <div className="max-w-6xl mx-auto">
        {/* command bar */}
        <div className="sticky top-14 z-20 -mx-8 px-8 py-3 bg-background/90 backdrop-blur border-b border-border mb-6 flex items-center justify-between gap-3" data-testid="lav-command-bar">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => (guardedNavigate ? guardedNavigate(-1) : nav(-1))} className="rounded-sm" data-testid="back-btn"><ArrowLeft size={18} /></Button>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-semibold" data-testid="lav-numero">{lav.numero_lavorazione}</span>
                {faseBadge(form.fase_lavorazione)}
                {lav.interventi_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1" data-testid="lav-intervento-chip"><Wrench size={11} /> {lav.interventi_count} intervent{lav.interventi_count === 1 ? "o" : "i"} collegat{lav.interventi_count === 1 ? "o" : "i"}</span>}
                {lav.is_sottolavorazione && <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700 border border-blue-200">sotto-lavorazione</span>}
              </div>
              <h1 className="font-heading text-lg font-bold">{form.titolo_lavorazione || "—"}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!lav.is_sottolavorazione && <Button variant="outline" onClick={del ? () => setDelOpen(true) : undefined} className="rounded-sm text-destructive gap-1" data-testid="delete-btn"><Trash size={15} /> Elimina</Button>}
            {lav.is_sottolavorazione && <Button variant="outline" onClick={() => setDelOpen(true)} className="rounded-sm text-destructive gap-1" data-testid="delete-btn"><Trash size={15} /> Elimina</Button>}
            <Button onClick={save} disabled={saving} className="rounded-sm gap-2" data-testid="save-btn"><FloppyDisk size={16} /> {saving ? "Salvataggio…" : "Salva"}</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            {/* A. Dati principali */}
            <Card className="rounded-sm p-5 shadow-none" data-testid="section-dati">
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider mb-3">Dati principali</h2>
              {prevBox && (
                <div className="mb-4 rounded-sm border border-blue-200 bg-blue-50/60 p-3" data-testid="lav-preventivo-box">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-xs font-semibold uppercase tracking-wider text-blue-800 flex items-center gap-1.5"><Receipt size={14} /> Preventivo collegato</span>
                    <Link to={`/commerciale/preventivi`} className="text-xs text-primary hover:underline" data-testid="lav-apri-preventivo">Vai ai preventivi</Link>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
                    <div><span className="text-muted-foreground">Numero:</span> <b>{prevBox.numero_preventivo}</b></div>
                    <div><span className="text-muted-foreground">Stato:</span> {prevBox.stato}</div>
                    <div className="col-span-2"><span className="text-muted-foreground">Cliente:</span> {prevBox.cliente_nome}</div>
                    {prevBox.data_accettazione && <div className="col-span-2"><span className="text-muted-foreground">Accettato il:</span> {fmtDate(prevBox.data_accettazione)}</div>}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs gap-1" onClick={openPrevPdf} data-testid="lav-apri-pdf-btn"><Paperclip size={13} /> Apri PDF preventivo</Button>
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs gap-1" onClick={downloadPrevPdf} data-testid="lav-scarica-pdf-btn"><DownloadSimple size={13} /> Scarica PDF</Button>
                  </div>
                  {!prevBox.pdf_presente && <p className="text-[11px] text-amber-600 mt-1.5">PDF preventivo non disponibile.</p>}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Cliente</Label><Input value={lav.cliente_nome} disabled className="mt-1 rounded-sm bg-secondary/50" data-testid="dati-cliente" /></div>
                <div><Label className="text-xs">Commerciale</Label>
                  <Select value={form.commerciale_id || "none"} onValueChange={(v) => upd("commerciale_id", v === "none" ? "" : v)}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="dati-commerciale"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">—</SelectItem>{tecnici.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Data conferma ordine *</Label><Input type="date" value={form.data_conferma_ordine} onChange={(e) => upd("data_conferma_ordine", e.target.value)} className="mt-1 rounded-sm" data-testid="dati-data-conferma" /></div>
                <div><Label className="text-xs">Importo vendita (€)</Label><Input type="number" min="0" step="0.01" value={form.importo_vendita} onChange={(e) => upd("importo_vendita", e.target.value)} className="mt-1 rounded-sm" data-testid="dati-importo" /></div>
                <div className="col-span-2"><Label className="text-xs">Titolo *</Label><Input value={form.titolo_lavorazione} onChange={(e) => upd("titolo_lavorazione", e.target.value)} className="mt-1 rounded-sm" data-testid="dati-titolo" /></div>
                <div className="col-span-2"><Label className="text-xs">Descrizione</Label><div className="mt-1"><RichEditor value={form.descrizione_lavorazione} onChange={(v) => upd("descrizione_lavorazione", v)} placeholder="Descrivi la lavorazione…" testid="dati-descrizione" /></div></div>
                <div className="col-span-2 flex items-center gap-2 pt-1">
                  <Switch checked={form.visibile_area_cliente} onCheckedChange={(v) => upd("visibile_area_cliente", v)} data-testid="dati-visibile" />
                  <Label className="text-sm">Visibile nell'area cliente</Label>
                </div>
              </div>
            </Card>

            {/* B. Conferma d'ordine */}
            <DropZone onFiles={(fl) => onUpload({ target: { files: fl, value: "" } })} className="block">
            <Card className="rounded-sm p-5 shadow-none" data-testid="section-allegati">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading font-semibold text-sm uppercase tracking-wider">Conferma d'ordine e allegati</h2>
                <input ref={fileRef} type="file" multiple className="hidden" onChange={onUpload} data-testid="allegato-input" />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="rounded-sm gap-1" data-testid="upload-allegato-btn"><Paperclip size={14} /> Carica file</Button>
              </div>
              {(lav.allegati_conferma_ordine || []).length === 0 ? <p className="text-sm text-muted-foreground">Nessun allegato. Carica la conferma d'ordine (max 100 MB per file).</p> :
                <div className="space-y-2">
                  {lav.allegati_conferma_ordine.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 p-2 rounded-sm border border-border" data-testid={`allegato-${a.id}`}>
                      <Paperclip size={15} className="text-muted-foreground shrink-0" />
                      <span className="text-sm flex-1 truncate">{a.nome_originale}</span>
                      <span className="text-[11px] text-muted-foreground">{Math.round((a.size || 0) / 1024)} KB</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => downloadAllegato(id, a)} data-testid={`download-allegato-${a.id}`}><DownloadSimple size={15} /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => delAllegato(a.id)} data-testid={`del-allegato-${a.id}`}><Trash size={14} /></Button>
                    </div>
                  ))}
                </div>}
            </Card>
            </DropZone>

            {/* D. Pianificazione tecnica */}
            <Card className="rounded-sm p-5 shadow-none" data-testid="section-tecnici">
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider mb-3">Pianificazione tecnica prevista</h2>
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-muted-foreground px-1">
                  <div className="col-span-4">Descrizione / ruolo</div><div className="col-span-3">Skill</div><div className="col-span-2">N° tecnici</div><div className="col-span-2">Ore/tecnico</div><div className="col-span-1 text-right">Tot.</div>
                </div>
                {form.righe_tecnici_previsti.map((r, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`riga-tecnico-${i}`}>
                    <div className="col-span-4"><MultiSelect options={RUOLO_OPTIONS.map((s) => ({ value: s, label: s }))} value={toArr(r.descrizione)} onChange={(v) => updRiga(i, "descrizione", v)} placeholder="Ruolo" width="w-full" testid={`riga-desc-${i}`} /></div>
                    <div className="col-span-3"><MultiSelect options={(cfg.skill || []).map((s) => ({ value: s, label: s }))} value={toArr(r.skill)} onChange={(v) => updRiga(i, "skill", v)} placeholder="Skill" width="w-full" testid={`riga-skill-${i}`} /></div>
                    <Input type="number" min="1" value={r.numero_tecnici} onChange={(e) => updRiga(i, "numero_tecnici", e.target.value)} className="col-span-2 rounded-sm h-9" data-testid={`riga-num-${i}`} />
                    <Input type="number" min="0" step="0.5" value={r.ore_per_tecnico} onChange={(e) => updRiga(i, "ore_per_tecnico", e.target.value)} className="col-span-2 rounded-sm h-9" data-testid={`riga-ore-${i}`} />
                    <div className="col-span-1 flex items-center justify-end gap-1">
                      <span className="text-sm font-medium" data-testid={`riga-tot-${i}`}>{rigaOre(r)}h</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeRiga(i)} data-testid={`riga-remove-${i}`}><Trash size={13} /></Button>
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addRiga} className="rounded-sm gap-1 mt-1" data-testid="add-riga-btn"><Plus size={14} /> Aggiungi riga tecnici</Button>
              </div>
              <div className="flex justify-between items-center mt-3 pt-3 border-t border-border">
                <div className="text-sm">Ore totali previste (lavorazione): <span className="font-bold text-base" data-testid="ore-proprie">{oreProprie}h</span></div>
                {lav.attivita_id && (
                  <div className="text-sm" data-testid="ore-consuntivate">Ore consuntivate (intervento):{" "}
                    <span className={`font-bold text-base ${(lav.ore_consuntivate || 0) > oreProprie && oreProprie > 0 ? "text-red-600" : "text-emerald-600"}`}>{lav.ore_consuntivate || 0}h</span>
                    {oreProprie > 0 && <span className="text-xs text-muted-foreground ml-1">({(lav.ore_consuntivate || 0) <= oreProprie ? "entro il previsto" : `+${Math.round(((lav.ore_consuntivate || 0) - oreProprie) * 100) / 100}h`})</span>}
                  </div>
                )}
              </div>
            </Card>

          </div>

          <div className="space-y-5">
            {/* G. Fase - spostato in alto */}
            <Card className="rounded-sm p-5 shadow-none" data-testid="section-fase">
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider mb-3">Fase lavorazione</h2>
              <Select value={form.fase_lavorazione} onValueChange={(v) => upd("fase_lavorazione", v)}>
                <SelectTrigger className="rounded-sm" data-testid="fase-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(cfg.fasi || []).filter((f) => f.attivo !== false).map((f) => <SelectItem key={f.key} value={f.key}><span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: f.color }} />{f.label}</span></SelectItem>)}</SelectContent>
              </Select>
              <div className="mt-3"><Label className="text-xs">Data prevista / target</Label><Input type="date" value={form.data_prevista} onChange={(e) => upd("data_prevista", e.target.value)} className="mt-1 rounded-sm" data-testid="data-prevista" /></div>
              <div className="mt-3"><Label className="text-xs">Note interne</Label><Textarea value={form.note_interne} onChange={(e) => upd("note_interne", e.target.value)} rows={3} className="mt-1 rounded-sm" data-testid="note-interne" /></div>
            </Card>

            {/* Opzioni operative (unito a Merce / fornitura) */}
            <Card className="rounded-sm p-5 shadow-none" data-testid="section-opzioni">
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider mb-3">Opzioni operative</h2>
              <div className="flex items-center gap-2 mb-3"><Switch checked={form.pernotto_esterno} onCheckedChange={(v) => upd("pernotto_esterno", v)} data-testid="pernotto-switch" /><Label className="text-sm flex items-center gap-1.5"><Moon size={15} /> Pernotto esterno</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.manovalanza_esterna} onCheckedChange={(v) => upd("manovalanza_esterna", v)} data-testid="manovalanza-switch" /><Label className="text-sm flex items-center gap-1.5"><UsersThree size={15} /> Manovalanza esterna</Label></div>
              <div className="flex items-center gap-2 mt-3"><Switch checked={form.remoto} onCheckedChange={(v) => upd("remoto", v)} data-testid="remoto-switch" /><Label className="text-sm flex items-center gap-1.5"><Headset size={15} /> Remoto</Label></div>
              <div className="mt-3">
                <div className="flex items-start gap-2 mb-3">
                  <Switch checked={form.solo_fornitura} onCheckedChange={(v) => upd("solo_fornitura", v)} data-testid="solo-fornitura-switch" />
                  <Label className="text-sm flex flex-col gap-0.5">
                    <span className="flex items-center gap-1.5"><Package size={15} /> Solo fornitura</span>
                    <span className="text-xs text-muted-foreground font-normal">(vendita/consegna materiale, nessuna attività tecnica)</span>
                  </Label>
                </div>
                <div><Label className="text-xs">Merce / materiale previsto</Label><Textarea value={form.merce_necessaria} onChange={(e) => upd("merce_necessaria", e.target.value)} rows={3} className="mt-1 rounded-sm" placeholder="Elenco materiali / apparati…" data-testid="merce-necessaria" /></div>
                <div className="mt-3"><Label className="text-xs">Stato merce *</Label>
                  <Select value={form.stato_merce} onValueChange={(v) => upd("stato_merce", v)}>
                    <SelectTrigger className="mt-1 rounded-sm" data-testid="stato-merce"><SelectValue /></SelectTrigger>
                    <SelectContent>{(cfg.stati_merce || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </Card>

            {/* Interventi collegati (molti) */}
            <Card className="rounded-sm p-5 shadow-none" data-testid="section-intervento">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading font-semibold text-sm uppercase tracking-wider flex items-center gap-2"><Wrench size={16} /> Interventi collegati {lav.interventi_count > 0 && <span className="text-xs text-muted-foreground">({lav.interventi_count})</span>}</h2>
              </div>

              {lav.all_interventi_completed && (
                <div className="mb-3 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2.5 flex items-start gap-2.5" data-testid="lav-completed-activities-banner">
                  <Warning size={20} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0 text-sm text-amber-900">
                    <div className="font-semibold">Tutti gli interventi collegati a questa lavorazione risultano conclusi.</div>
                    <div className="text-amber-800 mt-0.5 text-xs">Verifica l'esito e, se opportuno, imposta manualmente la lavorazione come "Lavorazione completata".</div>
                  </div>
                </div>
              )}

              {lav.sync_disattivata && (
                <div className="mb-3 rounded-sm border border-slate-300 bg-slate-50 px-3 py-2 flex items-start gap-2.5" data-testid="lav-sync-off-banner">
                  <LinkBreak size={18} className="text-slate-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0 text-xs text-slate-700">
                    <span className="font-semibold">Sincronizzazione automatica disattivata</span> — collegamenti multipli (più interventi o più lavorazioni). Gli stati di intervento e lavorazione vanno aggiornati manualmente.
                  </div>
                </div>
              )}

              {(lav.interventi_collegati || []).length === 0 ? (
                <p className="text-sm text-muted-foreground mb-3">Nessun intervento collegato. Apri il form Nuovo intervento precompilato: verifica cliente/sede/zona, imposta le sessioni e salva per collegarlo.</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {lav.interventi_collegati.map((it) => (
                    <div key={it.attivita_id} className="p-3 rounded-sm border border-border hover:bg-secondary/40" data-testid={`int-collegato-${it.attivita_id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-sm font-semibold shrink-0">n. {it.numero}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border shrink-0 ${it.stato === "completata" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-secondary text-foreground border-border"}`} data-testid={`int-stato-${it.attivita_id}`}>{intStatoLabel(it.stato)}</span>
                          {it.titolo && <span className="text-sm truncate">{it.titolo}</span>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => nav(it.link)} data-testid={`apri-intervento-btn-${it.attivita_id}`}><ArrowSquareOut size={15} /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => scollegaIntervento(it.attivita_id)} data-testid={`scollega-intervento-btn-${it.attivita_id}`}><LinkBreak size={14} /></Button>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-muted-foreground">
                        {it.cliente_nome && <span>{it.cliente_nome}</span>}
                        {it.data_sessione_recente && <span>Ultima sessione: {fmtDate(it.data_sessione_recente)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Button size="sm" onClick={createIntervento} className="rounded-sm gap-1.5 w-full h-8 text-xs" data-testid="crea-intervento-btn"><Plus size={13} /> Crea nuovo intervento</Button>
                <Button size="sm" variant="outline" onClick={() => { setIntLinkSel([]); setIntLinkDlg(true); searchLinkInt(""); }} className="rounded-sm gap-1.5 w-full h-8 text-xs" data-testid="collega-intervento-btn"><LinkSimple size={13} /> Collega intervento esistente</Button>
              </div>
            </Card>

            {/* Lavorazioni collegate */}
            <Card className="rounded-sm p-5 shadow-none" data-testid="section-lavorazioni-collegate">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading font-semibold text-sm uppercase tracking-wider flex items-center gap-2"><LinkSimple size={16} /> Lavorazioni collegate {(lav.lavorazioni_collegate_count || 0) > 0 && <span className="text-xs text-muted-foreground">({lav.lavorazioni_collegate_count})</span>}</h2>
              </div>
              {(lav.lavorazioni_collegate || []).length === 0 ? (
                <p className="text-sm text-muted-foreground mb-3">Nessuna lavorazione collegata. Collega altre lavorazioni correlate per ritrovarle rapidamente.</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {lav.lavorazioni_collegate.map((lc) => (
                    <div key={lc.id} className="p-3 rounded-sm border border-border hover:bg-secondary/40" data-testid={`lav-collegata-${lc.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-mono text-sm font-semibold shrink-0">{lc.numero}</span>
                          {lc.fase && faseBadge(lc.fase)}
                          {lc.titolo && <span className="text-sm truncate">{lc.titolo}</span>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => nav(lc.link)} data-testid={`apri-lav-collegata-${lc.id}`}><ArrowSquareOut size={15} /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => scollegaLavorazione(lc.id)} data-testid={`scollega-lav-${lc.id}`}><LinkBreak size={14} /></Button>
                        </div>
                      </div>
                      {lc.cliente_nome && <div className="mt-1 text-xs text-muted-foreground">{lc.cliente_nome}</div>}
                    </div>
                  ))}
                </div>
              )}
              <Button size="sm" variant="outline" onClick={() => { setLavLinkDlg(true); searchLinkLav(""); }} className="rounded-sm gap-1.5 w-full h-8 text-xs" data-testid="collega-lavorazione-btn"><LinkSimple size={13} /> Collega lavorazione esistente</Button>
            </Card>

            {/* I. Log */}
            <Card className="rounded-sm p-5 shadow-none" data-testid="section-log">
              <h2 className="font-heading font-semibold text-sm uppercase tracking-wider mb-3 flex items-center gap-2"><ClockCounterClockwise size={16} /> Log attività</h2>
              {logs.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna attività.</p> :
                <div className="space-y-2 max-h-96 overflow-auto">
                  {logs.map((l) => (
                    <div key={l.id} className="text-xs border-l-2 border-border pl-2.5 py-0.5" data-testid={`log-${l.id}`}>
                      <div className="font-medium">{ACTION_LABEL[l.action] || l.action}</div>
                      {l.old_value != null && <div className="text-muted-foreground">da <span className="line-through">{l.old_value}</span> a <b>{l.new_value}</b></div>}
                      {l.note && <div className="text-muted-foreground">{l.note}</div>}
                      <div className="text-muted-foreground/70">{fmtDate(l.timestamp)} · {l.user_name}</div>
                    </div>
                  ))}
                </div>}
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={intLinkDlg} onOpenChange={setIntLinkDlg}>
        <DialogContent className="rounded-sm max-w-xl" data-testid="link-intervento-dialog">
          <DialogHeader><DialogTitle>Collega intervento esistente</DialogTitle><DialogDescription>Cerca e seleziona uno o più interventi da collegare a questa lavorazione.</DialogDescription></DialogHeader>
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={intLinkQ} onChange={(e) => searchLinkInt(e.target.value)} placeholder="Cerca intervento per numero, richiesta, cliente…" className="pl-8 rounded-sm" data-testid="link-intervento-search" autoFocus />
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-border border border-border rounded-sm">
            {intLinkResults.length === 0 ? <p className="text-sm text-muted-foreground p-4">Nessun intervento trovato.</p> : intLinkResults.map((it) => {
              const already = (lav?.interventi_collegati || []).some((x) => x.attivita_id === it.id);
              const numero = it.numero || it.numero_intervento;
              const checked = intLinkSel.includes(it.id);
              return (
                <button key={it.id} onClick={() => !already && toggleIntLinkSel(it.id)} disabled={already} className="w-full text-left p-3 hover:bg-secondary flex items-center gap-3 disabled:opacity-50" data-testid={`int-link-result-${it.id}`}>
                  {already ? <Wrench size={18} className="text-muted-foreground shrink-0" />
                    : checked ? <CheckSquare size={18} weight="fill" className="text-blue-600 shrink-0" data-testid={`int-link-check-${it.id}`} />
                    : <Square size={18} className="text-muted-foreground shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">n. {numero} {already && <span className="text-[10px] text-emerald-600">(già collegato)</span>}</div>
                    <div className="text-xs text-muted-foreground truncate">{it.cliente_nome || "—"}{it.stato ? ` · ${intStatoLabel(it.stato)}` : ""}</div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between gap-2 pt-1">
            <span className="text-xs text-muted-foreground" data-testid="int-link-sel-count">{intLinkSel.length} selezionat{intLinkSel.length === 1 ? "o" : "i"}</span>
            <Button size="sm" disabled={intLinkSel.length === 0} onClick={confirmLinkSelection} className="rounded-sm gap-1.5" data-testid="int-link-confirm-selection-btn"><LinkSimple size={14} /> Collega selezionati{intLinkSel.length > 0 ? ` (${intLinkSel.length})` : ""}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={lavLinkDlg} onOpenChange={setLavLinkDlg}>
        <DialogContent className="rounded-sm max-w-xl" data-testid="link-lavorazione-dialog">
          <DialogHeader><DialogTitle>Collega lavorazione esistente</DialogTitle><DialogDescription>Cerca e seleziona una lavorazione da collegare a questa.</DialogDescription></DialogHeader>
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={lavLinkQ} onChange={(e) => searchLinkLav(e.target.value)} placeholder="Cerca lavorazione per numero, titolo, cliente…" className="pl-8 rounded-sm" data-testid="link-lavorazione-search" autoFocus />
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-border border border-border rounded-sm">
            {lavLinkResults.length === 0 ? <p className="text-sm text-muted-foreground p-4">Nessuna lavorazione trovata.</p> : lavLinkResults.map((lc) => {
              const already = (lav?.lavorazioni_collegate || []).some((x) => x.id === lc.id) || lc.id === id;
              return (
                <button key={lc.id} onClick={() => !already && linkLavorazione(lc.id)} disabled={already} className="w-full text-left p-3 hover:bg-secondary flex items-center gap-3 disabled:opacity-50" data-testid={`lav-link-result-${lc.id}`}>
                  <LinkSimple size={16} className="text-muted-foreground shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{lc.numero} {already && <span className="text-[10px] text-emerald-600">(già collegata)</span>}</div>
                    <div className="text-xs text-muted-foreground truncate">{lc.titolo || "—"}{lc.cliente_nome ? ` · ${lc.cliente_nome}` : ""}</div>
                  </div>
                  {!already && <LinkSimple size={15} className="text-blue-600 shrink-0" />}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={intLinkConfirm} onOpenChange={setIntLinkConfirm}>
        <AlertDialogContent className="rounded-sm" data-testid="int-link-multi-confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle>Collegare {intLinkSel.length} interventi?</AlertDialogTitle>
            <AlertDialogDescription>Stai per collegare {intLinkSel.length} interventi a questa lavorazione. Confermi?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="int-link-multi-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doLinkSelected} className="rounded-sm" data-testid="int-link-multi-confirm-btn">Collega {intLinkSel.length} interventi</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={intConfirmOpen} onOpenChange={setIntConfirmOpen}>
        <AlertDialogContent className="rounded-sm" data-testid="intervento-confirm-dialog">
          <AlertDialogHeader><AlertDialogTitle>{form?.solo_fornitura ? "Lavorazione Solo fornitura" : "Aprire il form Nuovo intervento?"}</AlertDialogTitle>
            <AlertDialogDescription>{form?.solo_fornitura
              ? "Questa lavorazione è marcata come Solo fornitura. Vuoi comunque aprire il form per creare un intervento tecnico?"
              : "Verrà aperto il form Nuovo intervento precompilato con i dati della lavorazione. Potrai verificare cliente/sede/zona, gestire le sessioni e salvare: l'intervento verrà collegato automaticamente alla lavorazione."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="intervento-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={createIntervento} className="rounded-sm" data-testid="intervento-confirm-btn">{form?.solo_fornitura ? "Procedi comunque" : "Apri form intervento"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Eliminare la lavorazione?</AlertDialogTitle>
            <AlertDialogDescription>{lav.is_sottolavorazione ? "La sotto-lavorazione verrà eliminata." : "La lavorazione e tutte le sue sotto-lavorazioni verranno eliminate. Operazione irreversibile."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="rounded-sm bg-destructive text-destructive-foreground" data-testid="confirm-delete-btn">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </InternalLayout>
  );
}
